import { useState, useEffect } from "react";
import {
  getMailsByRole,
  getNpcsByRole,
  quickRepliesByRole,
  npcReplyLogic,
  type MailThread,
  type MailMessage,
} from "../../../mocks/inbox";
import { useRole } from "../../../contexts/RoleContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface Props { mailId: string | null; }

// ─── 게임 효과 감지 로직 ──────────────────────────────────────────
type GameEffect = {
  type: "capital" | "reputation";
  delta: number;
  reason: string;
  icon: string;
};

function detectGameEffect(
  npcId: string,
  userText: string,
  npcResponse: string
): GameEffect | null {
  const ut = userText.toLowerCase();
  const nr = npcResponse;

  // 마케팅 — 광고주 협상 성공 (추가 협찬비 확보)
  if (npcId === "npc_mkt1" && nr.includes("500만원")) {
    return { type: "capital", delta: 500, reason: "마케팅 협찬 협상 성공! 추가 협찬비 +500만원 확보", icon: "ri-money-dollar-circle-line" };
  }
  // 마케팅 — 기자 언론 호보도 확보
  if (npcId === "npc_mkt2" && (nr.includes("메인 기사") || nr.includes("포털 메인") || nr.includes("선공개"))) {
    return { type: "reputation", delta: 12, reason: "언론 호보도 확보! 브랜드 명성 +12pt 상승", icon: "ri-newspaper-line" };
  }
  // 법무 — 독소조항 방어 성공
  if (npcId === "npc_leg1" && (ut.includes("22조") || ut.includes("31조") || ut.includes("독소") || ut.includes("삭제"))) {
    return { type: "reputation", delta: 8, reason: "독소 조항 방어 성공! 법적 리스크 감소로 신뢰도 +8pt", icon: "ri-shield-check-line" };
  }
  // 법무 — 투자 조건 유리하게 협상
  if (npcId === "npc_leg2" && nr.includes("3%")) {
    return { type: "capital", delta: 800, reason: "투자 조건 협상 성공! 유리한 조건 협의 +800만원 가치", icon: "ri-shake-hands-line" };
  }
  // A&R — 협력 계약 성사
  if (npcId === "npc_ar2" && (nr.includes("2,000만원") || nr.includes("미팅"))) {
    return { type: "capital", delta: 1500, reason: "A&R 협력 계약 진전! 잠재 계약금 +1,500만원 확보", icon: "ri-music-2-line" };
  }
  // A&R — 저작권 협의 진전
  if (npcId === "npc_ar1" && nr.includes("공연권")) {
    return { type: "reputation", delta: 5, reason: "저작권 협의 성공! 크리에이티브 신뢰도 +5pt", icon: "ri-copyright-line" };
  }
  // 재무 — 대출 승인
  if (npcId === "npc_fin2" && nr.includes("전액 승인")) {
    return { type: "capital", delta: 5000, reason: "대출 전액 승인! 유동성 자본금 +5,000만원 확보", icon: "ri-bank-line" };
  }
  return null;
}

// ─── 게임 이펙트 토스트 ───────────────────────────────────────────
function GameEffectToast({ effect, onHide }: { effect: GameEffect; onHide: () => void }) {
  useEffect(() => {
    const t = setTimeout(onHide, 3500);
    return () => clearTimeout(t);
  }, [onHide]);

  return (
    <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
      <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border shadow-xl ${
        effect.type === "capital"
          ? "bg-[#141416] border-[#4B6CF7]/30 text-[#7A9BFF]"
          : "bg-[#141416] border-white/20 text-[#F2F2F2]"
      }`}>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.06]">
          <i className={`${effect.icon} text-base`}></i>
        </div>
        <div>
          <div className="text-xs font-semibold">
            {effect.type === "capital" ? `자본금 +${effect.delta.toLocaleString()}만원` : `명성 +${effect.delta}pt`}
          </div>
          <div className="text-[10px] text-[#666666] mt-0.5">{effect.reason}</div>
        </div>
        <div className="text-lg font-bold ml-1">
          {effect.type === "capital" ? `+${effect.delta.toLocaleString()}만` : `+${effect.delta}pt`}
        </div>
      </div>
    </div>
  );
}

export default function MailDetail({ mailId }: Props) {
  const { jobRole } = useRole();
  const { updateCapital, updateReputation } = useCompany();
  const [replyText, setReplyText] = useState("");
  const [threads, setThreads] = useState<MailThread[]>([]);
  const [aiTyping, setAiTyping] = useState(false);
  const [gameEffect, setGameEffect] = useState<GameEffect | null>(null);

  useEffect(() => {
    setThreads(getMailsByRole(jobRole));
  }, [jobRole]);

  const npcs = getNpcsByRole(jobRole);
  const mail = threads.find((m) => m.id === mailId);
  const npc = npcs.find((c) => c.id === mail?.npcId);

  const handleSend = () => {
    if (!replyText.trim() || !mail || !npc) return;
    const userMsg: MailMessage = {
      id: `msg${Date.now()}`,
      from: "user",
      text: replyText,
      time: new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }),
    };
    const sentText = replyText;
    setThreads((prev) =>
      prev.map((m) => m.id === mail.id ? { ...m, isRead: true, messages: [...m.messages, userMsg] } : m)
    );
    setReplyText("");
    setAiTyping(true);

    setTimeout(async () => {
      const fn = npcReplyLogic[npc.id];
      const npcText = fn ? fn(sentText) : "확인했습니다. 추후 연락드리겠습니다.";
      const aiMsg: MailMessage = {
        id: `msg${Date.now() + 1}`,
        from: "npc",
        text: npcText,
        time: new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }),
      };
      setThreads((prev) =>
        prev.map((m) => m.id === mail.id ? { ...m, messages: [...m.messages, aiMsg] } : m)
      );
      setAiTyping(false);

      // ── 게임 효과 감지 및 적용 ──
      const effect = detectGameEffect(npc.id, sentText, npcText);
      if (effect) {
        setGameEffect(effect);
        if (effect.type === "capital") {
          await updateCapital(effect.delta);
        } else {
          await updateReputation(effect.delta);
        }
      }
    }, 2000);
  };

  const currentMail = threads.find((m) => m.id === mailId);
  const quickReplies = quickRepliesByRole[jobRole] ?? [];

  if (!currentMail || !npc) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/[0.03] mb-3">
          <i className="ri-mail-line text-2xl text-[#444444]"></i>
        </div>
        <p className="text-[#666666] text-sm">메일을 선택하면 내용이 표시됩니다</p>
        <p className="text-[#555555] text-xs mt-1">현재 직무: {jobRole}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/[0.05]">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.06] text-[#888888] text-sm font-semibold">
              {npc.avatar}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[#F2F2F2] font-medium text-sm">{npc.name}</span>
                <span className="text-[9px] text-[#666666] bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 rounded">
                  {npc.role}
                </span>
              </div>
              <p className="text-[#666666] text-xs">{npc.company}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className="w-7 h-7 flex items-center justify-center text-[#555555] hover:text-[#AAAAAA] cursor-pointer transition-colors">
              <i className="ri-star-line text-sm"></i>
            </button>
            <button type="button" className="w-7 h-7 flex items-center justify-center text-[#555555] hover:text-[#AAAAAA] cursor-pointer transition-colors">
              <i className="ri-archive-line text-sm"></i>
            </button>
          </div>
        </div>
        <h2 className="text-[#CCCCCC] font-medium text-sm mt-3">{currentMail.subject}</h2>
        <p className="text-[#666666] text-[10px] mt-0.5 italic">&ldquo;{npc.persona}&rdquo;</p>

        {/* 게임 효과 힌트 */}
        <div className="mt-2 flex items-center gap-1.5">
          <i className="ri-game-line text-[#555555] text-[10px]"></i>
          <span className="text-[#666666] text-[10px]">
            {npc.id === "npc_mkt1" && "협찬비 추가 협상 성공 시 자본금 보너스"}
            {npc.id === "npc_mkt2" && "언론 호보도 확보 시 명성 보너스"}
            {npc.id === "npc_leg1" && "독소조항(22조·31조) 방어 성공 시 명성 보너스"}
            {npc.id === "npc_leg2" && "투자 조건 유리하게 협상 시 자본금 보너스"}
            {npc.id === "npc_ar2" && "협력 계약 성사 시 자본금 보너스"}
            {npc.id === "npc_fin2" && "대출 전액 승인 유도 시 자본금 보너스"}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {currentMail.messages.map((msg) => (
          <div key={msg.id} className={`flex gap-3 ${msg.from === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg text-[10px] font-semibold shrink-0 ${
              msg.from === "npc" ? "bg-white/[0.06] text-[#888888]" : "bg-[#4B6CF7]/20 text-[#7A9BFF]"
            }`}>
              {msg.from === "npc" ? npc.avatar : "나"}
            </div>
            <div className={`max-w-[75%] flex flex-col ${msg.from === "user" ? "items-end" : "items-start"}`}>
              <div className={`px-4 py-3 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                msg.from === "user"
                  ? "bg-[#4B6CF7]/15 text-[#DDDDDD] border border-[#4B6CF7]/20 rounded-tr-sm"
                  : "bg-[#1E1E22] text-[#CCCCCC] border border-white/[0.08] rounded-tl-sm"
              }`}>
                {msg.text}
              </div>
              <span className="text-[#555555] text-[9px] mt-1">{msg.time}</span>
            </div>
          </div>
        ))}

        {aiTyping && (
          <div className="flex gap-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.06] text-[#888888] text-[10px] font-semibold">
              {npc.avatar}
            </div>
            <div className="bg-[#1E1E22] border border-white/[0.08] px-4 py-3 rounded-xl rounded-tl-sm">
              <div className="flex gap-1 items-center">
                {[0, 150, 300].map((d) => (
                  <div key={d} className="w-1.5 h-1.5 bg-[#4B6CF7]/50 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Replies */}
      <div className="px-5 py-2 flex gap-1.5 flex-wrap border-t border-white/[0.04]">
        {quickReplies.map((qr, i) => (
          <button key={i} type="button" onClick={() => setReplyText(qr)}
            className="text-[9px] px-2 py-1 rounded-full border border-white/[0.07] text-[#666666] hover:border-[#4B6CF7]/30 hover:text-[#7A9BFF] transition-colors cursor-pointer whitespace-nowrap">
            {qr}
          </button>
        ))}
      </div>

      {/* Reply Input */}
      <div className="px-5 py-3 border-t border-white/[0.05]">
        <div className="flex gap-2.5 items-end">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={`${npc.name}에게 답장...`}
            maxLength={500}
            rows={3}
            className="flex-1 bg-white/[0.03] text-[#CCCCCC] text-xs rounded-lg px-3 py-2.5 outline-none placeholder-[#555555] border border-white/[0.07] focus:border-[#4B6CF7]/30 resize-none"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend(); }}
          />
          <button type="button" onClick={handleSend} disabled={!replyText.trim()}
            className="h-10 w-10 flex items-center justify-center bg-[#4B6CF7] hover:bg-[#5B7CF7] disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-lg transition-colors cursor-pointer">
            <i className="ri-send-plane-fill text-sm"></i>
          </button>
        </div>
        <p className="text-[#666666] text-[9px] mt-1">Ctrl+Enter로 전송</p>
      </div>

      {/* 게임 이펙트 토스트 */}
      {gameEffect && <GameEffectToast effect={gameEffect} onHide={() => setGameEffect(null)} />}
    </div>
  );
}
